import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import axiosInstance from "../../../api/axios";
import useLikeClick from "../../../hooks/useLikeClick";
import useFavouriteClick from "../../../hooks/useFavouriteClick";
import { setVoteCount } from "../../../store/slices/lists.slice";
import { setLoginModal, setShareModal } from "../../../store/slices/modals.slice";
import { useAppSelector } from "../../../store/store";
import LoginModal from "../../../components/shared/LoginModal/LoginModal";
import ShareModal from "../../../components/shared/ShareModal/ShareModal";
import { ChatMessage } from "./chat.types";

const _PAGE_LOAD_ID = crypto.randomUUID();
const _CACHE_KEY = "chatCache";

interface ChatCache {
  loadId: string;
  messages: ChatMessage[];
  conversationId: number | null;
}

function readCache(): ChatCache | null {
  try {
    const raw = sessionStorage.getItem(_CACHE_KEY);
    if (!raw) return null;
    const cache: ChatCache = JSON.parse(raw);
    return cache.loadId === _PAGE_LOAD_ID ? cache : null;
  } catch {
    return null;
  }
}

function writeCache(messages: ChatMessage[], conversationId: number | null) {
  try {
    const cache: ChatCache = { loadId: _PAGE_LOAD_ID, messages, conversationId };
    sessionStorage.setItem(_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // ignore quota errors
  }
}

const langMap: Record<string, string> = {
  en: "en-US",
  ru: "ru-RU",
  uk: "uk-UA",
  ar: "ar-SA",
};

export function useChatTestPage() {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();

  const { shareModal, loginModal } = useAppSelector((state: any) => state.modals);

  const [shareMovieId, setShareMovieId] = useState<number | null>(null);

  const openLoginModal = () => {
    dispatch(setLoginModal(true));
  };

  const { likesList, likeHandler } = useLikeClick();
  const { favoritesList, favouritesHandler } = useFavouriteClick();

  const updateVoteCount = (id: any, key: any, type: any) => {
    dispatch(setVoteCount({ id, key, type }));
  };

  const [messages, setMessages] = useState<ChatMessage[]>(() => readCache()?.messages ?? []);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<number | null>(
    () => readCache()?.conversationId ?? null
  );
  const [sessionKey] = useState(
    () => localStorage.getItem("chatSessionKey") || crypto.randomUUID()
  );

  useEffect(() => {
    localStorage.setItem("chatSessionKey", sessionKey);
  }, [sessionKey]);

  useEffect(() => {
    writeCache(messages, conversationId);
  }, [messages, conversationId]);

  const [muted, setMuted] = useState(true);
  const [isChatExpanded, setIsChatExpanded] = useState(false);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userText = input.trim();
    setInput("");
    setIsChatExpanded(false);

    const userMsg: ChatMessage = {
      id: Date.now(),
      role: "user",
      content: userText,
      movie_ids: [],
      movies: [],
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      const payload: any = {
        message: userText,
        language: langMap[i18n.language] || "en-US",
      };
      if (conversationId) {
        payload.conversation_id = conversationId;
      }

      const { data } = await axiosInstance.post("chat/", payload, {
        headers: { "X-Chat-Session": sessionKey },
      });

      setConversationId(data.conversation_id);

      const assistantMsg: ChatMessage = {
        ...data.message,
        movies: data.movies || [],
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err: any) {
      const errorMsg: ChatMessage = {
        id: Date.now() + 1,
        role: "assistant",
        content: err.response?.data?.detail || "Something went wrong. Please try again.",
        movie_ids: [],
        movies: [],
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleRefreshMessage = (text: string) => {
    setInput(text);
  };

  const handleCopyMessage = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
  };

  const hasMovies = messages.some((msg) => msg.movies?.length > 0);
  const hasStarted = messages.length > 0 || loading;

  const lastMoviesIndex = messages.reduce(
    (acc, msg, idx) => (msg.movies && msg.movies.length > 0 ? idx : acc),
    -1
  );

  const lastUserMessageIndex = messages.reduce(
    (acc, msg, idx) => (msg.role === "user" ? idx : acc),
    -1
  );

  const moviesAreVisible =
    lastMoviesIndex !== -1 && lastMoviesIndex > lastUserMessageIndex && !loading;

  const showResultsColumn = hasMovies;
  const shouldShowOverlay = !moviesAreVisible || isChatExpanded;

  const carouselPropsBase = {
    likeHandler,
    favouritesHandler,
    likesList: likesList?.data?.results || [],
    favoritesList: favoritesList?.data?.results || [],
    updateVoteLocal: updateVoteCount,
    muted,
    onToggleMuted: () => setMuted((prev) => !prev),
    onShare: (id: number) => {
      setShareMovieId(id);
      dispatch(setShareModal(true));
    },
  };

  const chatMessagesRef = useRef<HTMLDivElement>(null);
  const mobileChatMessagesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [messages, loading]);

  useEffect(() => {
    if (!isChatExpanded) return;

    requestAnimationFrame(() => {
      mobileChatMessagesRef.current?.scrollTo({
        top: mobileChatMessagesRef.current.scrollHeight,
        behavior: "smooth",
      });
    });
  }, [isChatExpanded]);

  const modals = (
    <>
      {loginModal && <LoginModal />}
      {shareModal && shareMovieId && (
        <ShareModal
          currentId={shareMovieId}
          shareHandler={() => {}}
          shared={false}
          updateFieldLocal={() => {}}
        />
      )}
    </>
  );

  return {
    t,
    messages,
    input,
    setInput,
    loading,
    sendMessage,
    handleKeyDown,
    handleRefreshMessage,
    handleCopyMessage,
    hasMovies,
    hasStarted,
    lastMoviesIndex,
    moviesAreVisible,
    showResultsColumn,
    shouldShowOverlay,
    isChatExpanded,
    setIsChatExpanded,
    carouselPropsBase,
    openLoginModal,
    chatMessagesRef,
    mobileChatMessagesRef,
    modals,
  };
}

export type UseChatTestPageReturn = ReturnType<typeof useChatTestPage>;
